import { useGetProperLeverageMargin } from '@ui/features/Trade/hooks/useGetProperLeverageMargin';
import { usePriceLatest } from '@ui/hooks/price/usePriceLatest';
import { TradePair } from '@ui/types/tradePair';
import { Nullable } from '@ui/types/utility';
import { getMaxInputLeverageMarginNotionalUsd } from '@ui/utils/trade/getMaxInputLeverageMarginNotionalUsd';
import { getMaxInputMaxLongOIUsd } from '@ui/utils/trade/getMaxInputMaxLongOIUsd';
import { getMaxInputMaxShortOIUsd } from '@ui/utils/trade/getMaxInputMaxShortOIUsd';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';

import { useGetInputAmount } from './useGetInputAmount';

export const useMaxInput = ({
  pairInfos,
  leverage,
  payTokenPrice,
  spreadPercentage,
}: {
  pairInfos: Nullable<TradePair>;
  leverage: Nullable<number>;
  payTokenPrice: Nullable<BigNumber>;
  spreadPercentage: Nullable<number>;
}) => {
  const maxInputLeverageMarginNotionalUsd =
    useMaxInputLeverageMarginNotionalUsd({
      pairInfos,
      leverage,
      spreadPercentage,
    });
  const maxInputLeverageMarginNotional = useMaxInputLeverageMarginNotional({
    pairInfos,
    leverage,
    payTokenPrice,
    spreadPercentage,
  });

  const maxInputMaxLongOI = useMaxInputMaxLongOI({
    pairInfos,
    leverage,
    payTokenPrice,
    spreadPercentage,
  });
  const maxInputMaxLongOIUsd = useMaxInputMaxLongOIUsd({
    pairInfos,
    leverage,
    spreadPercentage,
  });

  const maxInputMaxShortOI = useMaxInputMaxShortOI({
    pairInfos,
    leverage,
    payTokenPrice,
    spreadPercentage,
  });
  const maxInputMaxShortOIUsd = useMaxInputMaxShortOIUsd({
    pairInfos,
    leverage,
    spreadPercentage,
  });

  return {
    leverageMarginNotionalMaxLimit: {
      token: maxInputLeverageMarginNotional,
      usd: maxInputLeverageMarginNotionalUsd,
    },
    maxInputMaxLongOILimit: {
      token: maxInputMaxLongOI,
      usd: maxInputMaxLongOIUsd,
    },
    maxInputMaxShortOILimit: {
      token: maxInputMaxShortOI,
      usd: maxInputMaxShortOIUsd,
    },
  };
};

export const useMaxInputLeverageMarginNotionalUsd = ({
  pairInfos,
  leverage,
  spreadPercentage,
}: {
  pairInfos: Nullable<TradePair>;
  leverage: Nullable<number>;
  spreadPercentage: Nullable<number>;
}) => {
  const targetLeverageMargin = useGetProperLeverageMargin(pairInfos, leverage);

  return useMemo(() => {
    if (
      isNil(targetLeverageMargin) ||
      isNil(leverage) ||
      isNil(spreadPercentage)
    )
      return;

    return getMaxInputLeverageMarginNotionalUsd({
      leverage,
      leverageMargin: targetLeverageMargin,
      spreadPercentage,
    });
  }, [leverage, targetLeverageMargin, spreadPercentage]);
};

export const useMaxInputLeverageMarginNotional = ({
  pairInfos,
  leverage,
  payTokenPrice,
  spreadPercentage,
}: {
  pairInfos: Nullable<TradePair>;
  leverage: Nullable<number>;
  payTokenPrice: Nullable<BigNumber>;
  spreadPercentage: Nullable<number>;
}) => {
  const maxInputLeverageMarginNotionalUsd =
    useMaxInputLeverageMarginNotionalUsd({
      pairInfos,
      leverage,
      spreadPercentage,
    });

  return useGetInputAmount(maxInputLeverageMarginNotionalUsd, payTokenPrice);
};

export const useMaxInputMaxLongOI = ({
  pairInfos,
  leverage,
  payTokenPrice,
  spreadPercentage,
}: {
  pairInfos: Nullable<TradePair>;
  leverage: Nullable<number>;
  payTokenPrice: Nullable<BigNumber>;
  spreadPercentage: Nullable<number>;
}) => {
  const maxInputMaxLongOIUsd = useMaxInputMaxLongOIUsd({
    pairInfos,
    leverage,
    spreadPercentage,
  });

  return useGetInputAmount(maxInputMaxLongOIUsd, payTokenPrice);
};

export const useMaxInputMaxLongOIUsd = ({
  pairInfos,
  leverage,
  spreadPercentage,
}: {
  pairInfos: Nullable<TradePair>;
  leverage: Nullable<number>;
  spreadPercentage: Nullable<number>;
}) => {
  const maxLongOiUsdBuffered = pairInfos?.maxLongOiUsdBuffered;
  const longOiQtyExisting = pairInfos?.longQty;
  const cachedMarketPrice = usePriceLatest(pairInfos?.baseSymbol);

  return useMemo(() => {
    if (
      isNil(longOiQtyExisting) ||
      isNil(maxLongOiUsdBuffered) ||
      isNil(cachedMarketPrice) ||
      isNil(leverage) ||
      isNil(spreadPercentage)
    )
      return;

    // for trouble shooting
    // console.table({
    //   maxLongOiUsdBuffered: maxLongOiUsdBuffered.toString(),
    //   longOiQtyExisting: longOiQtyExisting.toString(),
    //   cachedMarketPrice: cachedMarketPrice.toString(),
    // });

    return getMaxInputMaxLongOIUsd({
      leverage,
      longQty: longOiQtyExisting,
      maxLongOiUsdBuffered,
      pairPrice: cachedMarketPrice,
      spreadPercentage,
    });
  }, [
    longOiQtyExisting,
    maxLongOiUsdBuffered,
    cachedMarketPrice,
    leverage,
    spreadPercentage,
  ]);
};

export const useMaxInputMaxShortOI = ({
  pairInfos,
  leverage,
  payTokenPrice,
  spreadPercentage,
}: {
  pairInfos: Nullable<TradePair>;
  leverage: Nullable<number>;
  payTokenPrice: Nullable<BigNumber>;
  spreadPercentage: Nullable<number>;
}) => {
  const maxInputMaxShortOIUsd = useMaxInputMaxShortOIUsd({
    pairInfos,
    leverage,
    spreadPercentage,
  });

  return useGetInputAmount(maxInputMaxShortOIUsd, payTokenPrice);
};

export const useMaxInputMaxShortOIUsd = ({
  pairInfos,
  leverage,
  spreadPercentage,
}: {
  pairInfos: Nullable<TradePair>;
  leverage: Nullable<number>;
  spreadPercentage: Nullable<number>;
}) => {
  const maxShortOiUsdBuffered = pairInfos?.maxShortOiUsdBuffered;
  const shortOiQtyExisting = pairInfos?.shortQty;
  const cachedMarketPrice = usePriceLatest(pairInfos?.baseSymbol);

  return useMemo(() => {
    if (
      isNil(shortOiQtyExisting) ||
      isNil(maxShortOiUsdBuffered) ||
      isNil(cachedMarketPrice) ||
      isNil(leverage) ||
      isNil(spreadPercentage)
    )
      return;

    return getMaxInputMaxShortOIUsd({
      leverage,
      maxShortOiUsdBuffered,
      pairPrice: cachedMarketPrice,
      shortQty: shortOiQtyExisting,
      spreadPercentage,
    });
  }, [
    shortOiQtyExisting,
    maxShortOiUsdBuffered,
    cachedMarketPrice,
    leverage,
    spreadPercentage,
  ]);
};
